import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Switch,
  Divider,
  Tabs,
  Card,
  List,
  Spin,
  Space,
  Modal,
  TimePicker,
  Select,
  Row,
  Col,
  Typography,
  Statistic,
  Tag
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  DeleteOutlined,
  PlusOutlined,
  CarOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  EnvironmentOutlined,
  EditOutlined
} from '@ant-design/icons';
import { deliveryPersonsApi } from '../../utils/api';
import { message } from 'antd';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const DeliveryPersonForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isNewPerson = id === 'new';

  // État du formulaire
  const [loading, setLoading] = useState(!isNewPerson);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState('1');

  // Données du formulaire
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    identification_number: '',
    vehicle_description: '',
    vehicle_plate: '',
    transport_capacity: '',
    is_active: true,
    notes: '',
    profile_photo_url: ''
  });

  // États pour les zones et disponibilités
  const [zones, setZones] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [stats, setStats] = useState(null);

  // États pour les dialogues
  const [zoneModalVisible, setZoneModalVisible] = useState(false);
  const [newZone, setNewZone] = useState({ zone_name: '', is_preferred: false, notes: '' });
  const [availabilityModalVisible, setAvailabilityModalVisible] = useState(false);
  const [newAvailability, setNewAvailability] = useState({
    day_of_week: 1,
    time_from: '08:00',
    time_to: '18:00',
    is_available: true
  });

  // Chargement initial des données
  useEffect(() => {
    if (!isNewPerson) {
      loadDeliveryPerson();
    }
  }, [id]);

  const loadDeliveryPerson = async () => {
    try {
      setLoading(true);
      const response = await deliveryPersonsApi.getById(id);
      const data = response.data;
      
      // Mise à jour des données du formulaire
      const formValues = {
        name: data.name || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        identification_number: data.identification_number || '',
        vehicle_description: data.vehicle_description || '',
        vehicle_plate: data.vehicle_plate || '',
        transport_capacity: data.transport_capacity || '',
        is_active: data.is_active !== undefined ? data.is_active : true,
        notes: data.notes || '',
        profile_photo_url: data.profile_photo_url || ''
      };
      
      setFormData(formValues);
      form.setFieldsValue(formValues);
      
      // Chargement des zones et disponibilités
      if (data.zones) {
        setZones(data.zones);
      }
      
      if (data.availabilities) {
        setAvailabilities(data.availabilities);
      }
      
      if (data.stats) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du livreur:', error);
      message.error('Erreur lors du chargement du livreur');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setTabValue(key);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    
    try {
      let response;
      
      // Création ou mise à jour du livreur
      if (isNewPerson) {
        response = await deliveryPersonsApi.create(values);
        const newId = response.data.id;
        
        // Ajouter les zones si définies
        if (zones.length > 0) {
          for (const zone of zones) {
            await deliveryPersonsApi.addZone(newId, zone);
          }
        }
        
        // Ajouter les disponibilités si définies
        if (availabilities.length > 0) {
          for (const availability of availabilities) {
            await deliveryPersonsApi.updateAvailability(newId, availability);
          }
        }
        
        message.success('Livreur créé avec succès');
        navigate(`/delivery-persons/${newId}`);
      } else {
        await deliveryPersonsApi.update(id, values);
        message.success('Livreur mis à jour avec succès');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Gestion des zones
  const handleAddZone = async () => {
    if (!newZone.zone_name) {
      message.error('Le nom de la zone est obligatoire');
      return;
    }
    
    try {
      if (isNewPerson) {
        // Si c'est un nouveau livreur, ajouter à l'état local seulement
        setZones(prev => [...prev, newZone]);
      } else {
        // Sinon, appeler l'API
        const response = await deliveryPersonsApi.addZone(id, newZone);
        setZones(prev => [...prev, response.data]);
      }
      
      setNewZone({ zone_name: '', is_preferred: false, notes: '' });
      setZoneModalVisible(false);
      message.success('Zone ajoutée avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la zone:', error);
      message.error('Erreur lors de l\'ajout de la zone');
    }
  };

  const handleDeleteZone = async (zoneId, index) => {
    try {
      if (isNewPerson || !zoneId) {
        // Si c'est un nouveau livreur, supprimer de l'état local
        setZones(prev => prev.filter((_, i) => i !== index));
      } else {
        // Sinon, appeler l'API
        await deliveryPersonsApi.deleteZone(id, zoneId);
        setZones(prev => prev.filter(z => z.id !== zoneId));
      }
      
      message.success('Zone supprimée avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression de la zone:', error);
      message.error('Erreur lors de la suppression de la zone');
    }
  };

  // Gestion des disponibilités
  const handleAddAvailability = async () => {
    try {
      if (isNewPerson) {
        // Vérifier si le jour existe déjà
        const existingIndex = availabilities.findIndex(a => a.day_of_week === newAvailability.day_of_week);
        
        if (existingIndex >= 0) {
          // Mettre à jour la disponibilité existante
          setAvailabilities(prev => {
            const updated = [...prev];
            updated[existingIndex] = newAvailability;
            return updated;
          });
        } else {
          // Ajouter une nouvelle disponibilité
          setAvailabilities(prev => [...prev, newAvailability]);
        }
      } else {
        // Appeler l'API pour mettre à jour/ajouter la disponibilité
        const response = await deliveryPersonsApi.updateAvailability(id, newAvailability);
        
        // Mettre à jour l'état local
        const existingIndex = availabilities.findIndex(a => a.day_of_week === newAvailability.day_of_week);
        
        if (existingIndex >= 0) {
          setAvailabilities(prev => {
            const updated = [...prev];
            updated[existingIndex] = response.data;
            return updated;
          });
        } else {
          setAvailabilities(prev => [...prev, response.data]);
        }
      }
      
      setAvailabilityModalVisible(false);
      message.success('Disponibilité mise à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la disponibilité:', error);
      message.error('Erreur lors de la mise à jour de la disponibilité');
    }
  };

  // Obtenir le nom du jour de la semaine
  const getDayName = (dayOfWeek) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[dayOfWeek] || '';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            type="link" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/delivery-persons')}
          />
          <Title level={2}>
            {isNewPerson ? 'Nouveau Livreur' : `Modifier ${formData.name}`}
          </Title>
        </Space>
      </div>

      <Card>
        <Tabs activeKey={tabValue} onChange={handleTabChange}>
          <TabPane tab="Informations" key="1">
            <Form
              form={form}
              layout="vertical"
              initialValues={formData}
              onFinish={handleSubmit}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Nom"
                    rules={[{ required: true, message: 'Le nom est obligatoire' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Nom complet" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="Téléphone"
                    rules={[{ required: true, message: 'Le téléphone est obligatoire' }]}
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="Numéro de téléphone" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[{ type: 'email', message: 'Format d\'email invalide' }]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Adresse email" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="identification_number"
                    label="Numéro d'identification"
                  >
                    <Input placeholder="Numéro d'identification" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="address"
                    label="Adresse"
                  >
                    <TextArea
                      placeholder="Adresse complète"
                      autoSize={{ minRows: 2, maxRows: 4 }}
                      prefix={<EnvironmentOutlined />}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider>Informations du véhicule</Divider>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name="vehicle_description"
                    label="Description du véhicule"
                  >
                    <Input prefix={<CarOutlined />} placeholder="Type de véhicule" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="vehicle_plate"
                    label="Plaque d'immatriculation"
                  >
                    <Input placeholder="Numéro de plaque" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="transport_capacity"
                    label="Capacité de transport"
                  >
                    <Input placeholder="Ex: 10 colis, 100kg, etc." />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Divider />
                </Col>

                <Col span={24}>
                  <Form.Item
                    name="notes"
                    label="Notes"
                  >
                    <TextArea
                      placeholder="Notes additionnelles"
                      autoSize={{ minRows: 3, maxRows: 6 }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name="is_active"
                    valuePropName="checked"
                  >
                    <Switch 
                      checkedChildren="Actif" 
                      unCheckedChildren="Inactif"
                    /> Livreur actif
                  </Form.Item>
                </Col>

                <Col span={24} style={{ textAlign: 'right' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={saving}
                  >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </Col>
              </Row>
            </Form>
          </TabPane>

          <TabPane tab="Zones" key="2">
            <div style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={4}>Zones de livraison</Title>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setZoneModalVisible(true)}
                >
                  Ajouter une zone
                </Button>
              </Space>
            </div>

            {zones.length === 0 ? (
              <div style={{ textAlign: 'center', margin: '32px 0', color: 'rgba(0, 0, 0, 0.45)' }}>
                <Text>Aucune zone définie</Text>
              </div>
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={zones}
                renderItem={(zone, index) => (
                  <List.Item
                    actions={[
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteZone(zone.id, index)}
                      />
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          {zone.zone_name}
                          {zone.is_preferred && <Tag color="blue">Zone préférentielle</Tag>}
                        </Space>
                      }
                      description={zone.notes}
                    />
                  </List.Item>
                )}
              />
            )}
          </TabPane>

          <TabPane tab="Disponibilités" key="3">
            <div style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={4}>Disponibilités</Title>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setAvailabilityModalVisible(true)}
                >
                  Ajouter une disponibilité
                </Button>
              </Space>
            </div>

            {availabilities.length === 0 ? (
              <div style={{ textAlign: 'center', margin: '32px 0', color: 'rgba(0, 0, 0, 0.45)' }}>
                <Text>Aucune disponibilité définie</Text>
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {[0, 1, 2, 3, 4, 5, 6].map(day => {
                  const dayAvailability = availabilities.find(a => a.day_of_week === day);
                  
                  return (
                    <Col span={8} key={day}>
                      <Card
                        title={getDayName(day)}
                        extra={
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => {
                              setNewAvailability({
                                day_of_week: day,
                                time_from: dayAvailability?.time_from || '08:00',
                                time_to: dayAvailability?.time_to || '18:00',
                                is_available: dayAvailability?.is_available !== undefined ? dayAvailability.is_available : true
                              });
                              setAvailabilityModalVisible(true);
                            }}
                          />
                        }
                        style={{
                          opacity: dayAvailability?.is_available ? 1 : 0.6,
                          background: dayAvailability?.is_available ? 'white' : '#f5f5f5'
                        }}
                      >
                        {dayAvailability ? (
                          <>
                            {dayAvailability.is_available ? (
                              <Text>{dayAvailability.time_from} - {dayAvailability.time_to}</Text>
                            ) : (
                              <Text type="secondary">Non disponible</Text>
                            )}
                          </>
                        ) : (
                          <Text type="secondary">Non défini</Text>
                        )}
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </TabPane>

          {!isNewPerson && (
            <TabPane tab="Statistiques" key="4">
              <Title level={4}>Statistiques de performance</Title>
              
              {stats ? (
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="Livraisons totales"
                        value={stats.total_deliveries}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="Livraisons réussies"
                        value={stats.successful_deliveries}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="Livraisons échouées"
                        value={stats.failed_deliveries}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic
                        title="Temps moyen"
                        value={stats.avg_delivery_time}
                        suffix="min"
                      />
                    </Card>
                  </Col>
                  <Col span={24}>
                    <Card>
                      <Statistic
                        title="Paiements collectés"
                        value={stats.total_payments_collected}
                        precision={2}
                        prefix="€"
                      />
                    </Card>
                  </Col>
                </Row>
              ) : (
                <div style={{ textAlign: 'center', margin: '32px 0', color: 'rgba(0, 0, 0, 0.45)' }}>
                  <Text>Aucune statistique disponible</Text>
                </div>
              )}
            </TabPane>
          )}
        </Tabs>
      </Card>

      {/* Modal pour ajouter une zone */}
      <Modal
        title="Ajouter une zone de livraison"
        visible={zoneModalVisible}
        onCancel={() => setZoneModalVisible(false)}
        onOk={handleAddZone}
        okText="Ajouter"
        cancelText="Annuler"
      >
        <Form layout="vertical">
          <Form.Item
            label="Nom de la zone"
            required
          >
            <Input
              value={newZone.zone_name}
              onChange={(e) => setNewZone({ ...newZone, zone_name: e.target.value })}
              placeholder="Nom de la zone"
            />
          </Form.Item>
          <Form.Item
            label="Notes"
          >
            <TextArea
              value={newZone.notes}
              onChange={(e) => setNewZone({ ...newZone, notes: e.target.value })}
              placeholder="Notes additionnelles"
              rows={2}
            />
          </Form.Item>
          <Form.Item>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={newZone.is_preferred}
                onChange={(checked) => setNewZone({ ...newZone, is_preferred: checked })}
              />
              <span style={{ marginLeft: 8 }}>Zone préférentielle</span>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal pour gérer une disponibilité */}
      <Modal
        title={`Gérer la disponibilité - ${getDayName(newAvailability.day_of_week)}`}
        visible={availabilityModalVisible}
        onCancel={() => setAvailabilityModalVisible(false)}
        onOk={handleAddAvailability}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form layout="vertical">
          <Form.Item>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <Switch
                checked={newAvailability.is_available}
                onChange={(checked) => setNewAvailability({ ...newAvailability, is_available: checked })}
              />
              <span style={{ marginLeft: 8 }}>Disponible ce jour</span>
            </div>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="De">
                <TimePicker
                  value={newAvailability.time_from ? dayjs(newAvailability.time_from, 'HH:mm') : null}
                  onChange={(time, timeString) => setNewAvailability({ ...newAvailability, time_from: timeString })}
                  format="HH:mm"
                  disabled={!newAvailability.is_available}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="À">
                <TimePicker
                  value={newAvailability.time_to ? dayjs(newAvailability.time_to, 'HH:mm') : null}
                  onChange={(time, timeString) => setNewAvailability({ ...newAvailability, time_to: timeString })}
                  format="HH:mm"
                  disabled={!newAvailability.is_available}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default DeliveryPersonForm; 